import React from 'react';
import type { PropsWithChildren } from 'react';

import {
  AuthProvider as AuthStoreProvider,
  OauthCallbackLoginManager,
} from '@reckon-web/auth-store';

import { Config } from '../Config';
import { Loader } from '../../components/common/Loader';
import { Routes, matchesRoutePath } from '../Navigation/Routes';

import { UrlFragmentManager } from './UrlFragmentManager';

export type AuthProviderProps = PropsWithChildren<{
  storagePrefix?: string;
  logoutUrl?: string;
  nextParam?: string;
  clientId?: string;
}>;

// Hash params that must never be promoted into a server-visible query string.
const SENSITIVE_HASH_PARAMS = ['accessToken'];

/**
 * We forward the current page URL to identity as a `redirect_url` *query* param
 * so the customer returns here after logging in. Unlike a URL fragment, a query
 * string is sent to the identity server (access logs) and can leak via
 * referrers, so strip any token-like hash params (e.g. an `accessToken` the
 * fragment manager may still be consuming) before encoding the return URL.
 */
const buildSanitizedReturnUrl = (href: string) => {
  const url = new URL(href);
  const params = new URLSearchParams(url.hash.replace(/^#\??/, ''));
  SENSITIVE_HASH_PARAMS.forEach((param) => {
    return params.delete(param);
  });
  const rebuiltHash = params.toString();
  url.hash = rebuiltHash ? `?${rebuiltHash}` : '';
  return url.toString();
};

export function AuthProvider(props: AuthProviderProps) {
  /**
    const logoutUrl = props.logoutUrl ?? Config.APPLICATION_LOGIN_URL;
    const clientId = props.clientId ?? Config.APPLICATION_OAUTH_CLIENTID;
   */
  const storagePrefix =
    props.storagePrefix ?? Config.APPLICATION_LOCALSTORAGE_PREFIX;
  return (
    <AuthStoreProvider
      storagePrefix={storagePrefix}
      onLogout={(args) => {
        if (typeof window === 'undefined') {
          return false;
        }

        // Customer self-service migration entry: send them to identity login and
        // bring them back to this exact page (with subscriptionId/companyId hash)
        // after they authenticate. Identity appends the accessToken on return.
        const isMigrationEntry =
          args.reasonCode === 'SESSION_EXPIRED' &&
          matchesRoutePath(
            Routes.startMigration.path,
            window.location.pathname
          );

        if (isMigrationEntry) {
          const redirectUrl = encodeURIComponent(
            buildSanitizedReturnUrl(window.location.href)
          );
          window.location.href = `${Config.APPLICATION_LOGIN_URL}login?redirect_url=${redirectUrl}`;
          return;
        }

        window.location.href = `${Config.APPLICATION_LOGIN_URL}login`;
        return;
      }}
    >
      <UrlFragmentManager>
        <OauthCallbackLoginManager
          enabled={!Config.APPLICATION_FEATURE_USE_DEVLAUNCHER}
        >
          <AuthStoreProvider.IsLoading
            loader={<Loader label="Authenticating" />}
          >
            {props.children}
          </AuthStoreProvider.IsLoading>
        </OauthCallbackLoginManager>
      </UrlFragmentManager>
    </AuthStoreProvider>
  );
}
